/**
 * Readiness engine — the single source of truth for every number CLEAR shows.
 *
 * PRD rules implemented here:
 *  1. Each plan item has three gates (Paint, RM, Quality).
 *  2. Ready qty = min(Paint OK, RM OK, Quality OK). A gate nobody touched counts as 0.
 *  3. Every non-ready unit is charged to exactly one blocking gate: the gate with the
 *     lowest cleared quantity, ties broken Paint → RM → Quality. So the dashboard
 *     buckets (Ready + blocked at each gate) always add up to the plan.
 *
 * Quality can never clear more than Paint and RM have cleared ("clearable"), and it
 * unlocks only once clearable > 0. Every entry stores an explicit okQty, which makes
 * the plan-edit rules fall out of the arithmetic:
 *  - reducing the plan qty caps every gate at the new qty (min),
 *  - increasing it turns a full OK into an effective Partial and puts the item back
 *    on that department's To Do list.
 *
 * Everything here is pure and synchronous: the server uses it authoritatively and the
 * web app uses the same code for optimistic updates.
 */
import {
  GATES,
  PRIORITY_RANK,
  type Gate,
  type GateStatus,
  type ItemStatus,
  type PlanItemState,
} from './types';

/** Quantity a gate has effectively cleared, after caps. Pending = 0. */
export function effective(item: PlanItemState, gate: Gate): number {
  const e = item.gates[gate];
  if (!e) return 0;
  if (gate === 'quality') return Math.max(0, Math.min(e.okQty, clearable(item)));
  return Math.max(0, Math.min(e.okQty, item.qty));
}

/** Most Quality can clear: what both Paint and RM have cleared. */
export function clearable(item: PlanItemState): number {
  return Math.min(effective(item, 'paint'), effective(item, 'rm'));
}

export function isQualityUnlocked(item: PlanItemState): boolean {
  return clearable(item) > 0;
}

/** Upper bound for a gate's OK quantity right now. */
export function maxClearable(item: PlanItemState, gate: Gate): number {
  return gate === 'quality' ? clearable(item) : item.qty;
}

export function readyQty(item: PlanItemState): number {
  if (item.cancelled) return 0;
  return Math.min(effective(item, 'paint'), effective(item, 'rm'), effective(item, 'quality'));
}

/** The gate charged with the item's unready quantity, or null when fully ready. */
export function blockingGate(item: PlanItemState): Gate | null {
  if (readyQty(item) >= item.qty) return null;
  let best: Gate = 'paint';
  let bestV = effective(item, 'paint');
  for (const g of GATES) {
    const v = effective(item, g);
    if (v < bestV) {
      best = g;
      bestV = v;
    }
  }
  return best;
}

/**
 * Whether a gate is holding units back deliberately (red) rather than simply not
 * having been updated yet (amber). Paint/RM partial means "rest still to come"
 * (pending); Quality partial means the rest is held.
 */
export function isHold(item: PlanItemState, gate: Gate): boolean {
  const e = item.gates[gate];
  if (!e) return false;
  if (e.status === 'blocked') return true;
  return gate === 'quality' && e.status === 'partial';
}

export function itemStatus(item: PlanItemState): ItemStatus {
  if (item.startedAt != null) return 'STARTED';
  const r = readyQty(item);
  if (r >= item.qty) return 'READY';
  if (r > 0) return 'PART';
  const b = blockingGate(item);
  return b && isHold(item, b) ? 'HOLD' : 'WAIT';
}

/** When the gate started waiting (or when it last acted). */
export function pendingSince(item: PlanItemState, gate: Gate): number {
  const e = item.gates[gate];
  if (e) return e.at;
  if (gate === 'quality') {
    const p = item.gates.paint?.at ?? item.activeSince;
    const r = item.gates.rm?.at ?? item.activeSince;
    return Math.max(p, r);
  }
  return item.activeSince;
}

export type BlockKind = 'hold' | 'partial' | 'pending';

export interface Blocker {
  gate: Gate;
  kind: BlockKind;
  /** Units charged to this gate (plan qty − ready qty). */
  qty: number;
  since: number;
  reasonId: string | null;
}

/** Why an item is not fully ready, in one structured answer. */
export function blocker(item: PlanItemState): Blocker | null {
  const gate = blockingGate(item);
  if (!gate) return null;
  const e = item.gates[gate];
  const qty = item.qty - readyQty(item);
  if (isHold(item, gate)) return { gate, kind: 'hold', qty, since: e!.at, reasonId: e!.reasonId };
  if (e) return { gate, kind: 'partial', qty, since: e.at, reasonId: e.reasonId };
  return { gate, kind: 'pending', qty, since: pendingSince(item, gate), reasonId: null };
}

export interface GateBucket {
  hold: number;
  wait: number;
}

export interface PlanTotals {
  items: number;
  plan: number;
  ready: number;
  started: number;
  blocked: Record<Gate, GateBucket>;
  /** Units each gate has cleared (for department progress). */
  cleared: Record<Gate, number>;
  /** ready + all blocked buckets === plan. Always true unless there is a bug. */
  balanced: boolean;
}

export function planTotals(items: readonly PlanItemState[]): PlanTotals {
  const t: PlanTotals = {
    items: 0,
    plan: 0,
    ready: 0,
    started: 0,
    blocked: { paint: { hold: 0, wait: 0 }, rm: { hold: 0, wait: 0 }, quality: { hold: 0, wait: 0 } },
    cleared: { paint: 0, rm: 0, quality: 0 },
    balanced: true,
  };
  for (const item of items) {
    if (item.cancelled) continue;
    t.items += 1;
    t.plan += item.qty;
    const r = readyQty(item);
    t.ready += r;
    if (item.startedAt != null) t.started += item.qty;
    for (const g of GATES) t.cleared[g] += effective(item, g);
    const b = blockingGate(item);
    if (b) {
      const rest = item.qty - r;
      if (isHold(item, b)) t.blocked[b].hold += rest;
      else t.blocked[b].wait += rest;
    }
  }
  const sum = GATES.reduce((s, g) => s + t.blocked[g].hold + t.blocked[g].wait, t.ready);
  t.balanced = sum === t.plan;
  return t;
}

/** Floor order: priority, then the Planner's run order. */
export function floorSort<T extends PlanItemState>(items: readonly T[]): T[] {
  return items
    .slice()
    .sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || a.runOrder - b.runOrder);
}

export function runSort<T extends PlanItemState>(items: readonly T[]): T[] {
  return items.slice().sort((a, b) => a.runOrder - b.runOrder);
}

/**
 * Whether the gate owes an answer on this item right now:
 *  - never answered, or
 *  - answered OK but the plan (or, for Quality, the clearable qty) has since grown.
 */
export function needsAction(item: PlanItemState, gate: Gate): boolean {
  if (item.cancelled || item.startedAt != null) return false;
  const e = item.gates[gate];
  if (gate === 'quality') {
    if (!isQualityUnlocked(item)) return false;
    if (!e) return true;
    return e.status === 'ok' && e.okQty < clearable(item);
  }
  if (!e) return true;
  return e.status === 'ok' && e.okQty < item.qty;
}

export interface FloorQueues<T> {
  todo: T[];
  issues: T[];
  done: T[];
  /** Quality only: items not yet unlocked by Paint and RM. */
  waiting: T[];
}

export function floorQueues<T extends PlanItemState>(items: readonly T[], gate: Gate): FloorQueues<T> {
  const live = floorSort(items.filter((i) => !i.cancelled));
  const q: FloorQueues<T> = { todo: [], issues: [], done: [], waiting: [] };
  for (const item of live) {
    const e = item.gates[gate];
    if (needsAction(item, gate)) q.todo.push(item);
    else if (!e) {
      if (gate === 'quality' && item.startedAt == null) q.waiting.push(item);
    } else if (e.status === 'ok') q.done.push(item);
    else q.issues.push(item);
  }
  return q;
}

export type LockReason = 'started' | 'cancelled' | 'quality_acted';

/**
 * Edit lock. A department may correct its own answer until the next gate acts on it
 * (PRD "Forgiving"). Once Quality has acted, Paint/RM may still *progress* (clear more
 * units — e.g. the rest of a partial batch arrives) but may not reduce what Quality
 * has already inspected. Nothing changes after the line has started.
 */
export function editLock(item: PlanItemState, gate: Gate, nextEffective?: number): LockReason | null {
  if (item.cancelled) return 'cancelled';
  if (item.startedAt != null) return 'started';
  if (gate !== 'quality' && item.gates.quality && item.gates[gate]) {
    if (nextEffective === undefined || nextEffective < effective(item, gate)) return 'quality_acted';
  }
  return null;
}

export type GateInputError =
  | 'cancelled'
  | 'started'
  | 'quality_acted'
  | 'quality_locked'
  | 'qty_out_of_range'
  | 'reason_required'
  | 'reason_not_allowed';

export interface GateInput {
  status: GateStatus;
  okQty?: number | null;
  reasonId?: string | null;
}

export type GateValidation =
  { ok: true; okQty: number; reasonId: string | null } | { ok: false; error: GateInputError };

/** Validate a gate update against the item's current state and resolve the stored okQty. */
export function validateGateInput(item: PlanItemState, gate: Gate, input: GateInput): GateValidation {
  if (item.cancelled) return { ok: false, error: 'cancelled' };
  if (item.startedAt != null) return { ok: false, error: 'started' };
  if (gate === 'quality' && !isQualityUnlocked(item)) return { ok: false, error: 'quality_locked' };
  const max = maxClearable(item, gate);
  const reasonId = input.reasonId ?? null;
  let okQty: number;
  switch (input.status) {
    case 'ok':
      okQty = max;
      // Quality may attach one observation reason to an OK ("OK with observation").
      if (reasonId && gate !== 'quality') return { ok: false, error: 'reason_not_allowed' };
      break;
    case 'partial': {
      const v = input.okQty ?? NaN;
      if (!Number.isInteger(v) || v <= 0 || v >= max) return { ok: false, error: 'qty_out_of_range' };
      if (gate === 'quality' && !reasonId) return { ok: false, error: 'reason_required' };
      okQty = v;
      break;
    }
    case 'blocked':
      if (!reasonId) return { ok: false, error: 'reason_required' };
      okQty = 0;
      break;
  }
  const nextEff = gate === 'quality' ? Math.min(okQty, clearable(item)) : Math.min(okQty, item.qty);
  const lock = editLock(item, gate, nextEff);
  if (lock) return { ok: false, error: lock };
  return { ok: true, okQty, reasonId };
}

export function canStart(item: PlanItemState): boolean {
  return !item.cancelled && item.startedAt == null && readyQty(item) > 0;
}

/** Items that newly became READY/PART between two snapshots (Line Supervisor highlight). */
export function newlyRunnable(prev: readonly PlanItemState[], next: readonly PlanItemState[]): string[] {
  const before = new Map(prev.map((i) => [i.id, readyQty(i) > 0]));
  return next.filter((i) => readyQty(i) > 0 && before.get(i.id) === false).map((i) => i.id);
}

/** Age in ms of the oldest item waiting on this gate, or null when nothing is waiting. */
export function oldestPending(items: readonly PlanItemState[], gate: Gate, now: number): number | null {
  const todo = floorQueues(items, gate).todo;
  if (!todo.length) return null;
  return now - Math.min(...todo.map((i) => pendingSince(i, gate)));
}
